import React, { useState } from "react"
import {
    Alert, Box, Chip, CircularProgress, Grid,
    List, ListItem, ListItemText, Paper, Stack, Typography
} from "@mui/material"
import { format } from "date-fns"
import { getDateInServerTimeZone } from "../../core/utils/utils"
import {
    useGetExperienceQuery,
    useGetMyExperienceTransactionsQuery,
    useGetAllAchievementCategoriesQuery,
    useGetExperienceLevelsQuery,
    useGetExperienceCategoriesQuery,
    useGetMascotsQuery,
    useClaimRewardMutation,
    useGetActiveSeasonQuery,
    useGetMySeasonResultsQuery,
} from "../../core/api/experience-api"
import ClaimCelebration from "./ClaimCelebration"
import ProgressBar from "./ProgressBar"
import AppCopy from "../Common/AppCopyView"
import "./tokens.css"

function formatDate(s) {
    try { return format(getDateInServerTimeZone(s), "dd.MM.yyyy HH:mm") } catch { return s }
}

function txTypeLabel(t) {
    switch (t) {
        case "ACCRUAL": return "Accrual"
        case "ADJUST": return "Adjust"
        case "REWARD_CLAIM": return "Reward"
        case "RESET": return "Reset"
        default: return t
    }
}

// Mascot card with linked-category progress (CategoryMascotCard concept).
// Image on the left as background-cover; right side: title + description, level pill,
// XP value vs next-level threshold, linear progress bar, hint about next reward.
// Per product spec: no claim button — claims happen on the global progress bar.
export function MascotInfoCard({ mascot, category, xp = 0, levels = [] }) {
    // Derive level from the shared ExperienceLevels list against this category's xp.
    const sortedLevels = [...levels].sort((a, b) => a.level - b.level)
    const currentLevel = sortedLevels.reduce(
        (acc, l) => (xp >= l.minXp ? l.level : acc),
        0
    )
    const nextLevel = sortedLevels.find(l => l.level === currentLevel + 1) || null
    const nextThreshold = nextLevel ? nextLevel.minXp : (sortedLevels[sortedLevels.length - 1]?.minXp ?? 100)
    const remaining = nextLevel ? Math.max(0, nextThreshold - xp) : 0
    const pct = Math.min(100, (xp / Math.max(1, nextThreshold)) * 100)

    return (
        <Paper
            variant="outlined"
            sx={{
                position: "relative",
                background: "linear-gradient(135deg, #FDECE8 0%, #fff 60%)",
                borderColor: "#FF4000",
                borderWidth: 3,
                borderRadius: 5,
                overflow: "hidden",
                display: "flex",
                minHeight: 200,
                p:2
            }}
        >
            {/* Mascot image — anchored to bottom-center, fills the left column. */}
            <Box
                sx={{
                    width: { xs: 100, sm: 140},
                    pl:2,pb:2,pr:1,
                    flexShrink: 0,
                    backgroundImage: mascot.imageUrl ? `url(${mascot.imageUrl})` : "none",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center center",
                }}
            />

            <Box sx={{ flex: 1, p: 2, pl: 2, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography component={"h3"} className={"title"} sx={{ pb:1, fontWeight: 700, fontSize: { xs: 16, sm: 19 }, color: "var(--xp-orange)", lineHeight: 1.1 }}>
                            {mascot.title}
                        </Typography>
                        {/* Без line-clamp: описания белок сезона 2 длиннее трёх
                            строк и должны быть видны целиком. */}
                        {(mascot.description || category?.description) && (
                            <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.4, lineHeight: 1.35 }}>
                                {mascot.description || category?.description}
                            </Typography>
                        )}
                    </Box>
                    {/*<Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ flexShrink: 0 }}>*/}
                    {/*    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.secondary", letterSpacing: ".1em" }}>LVL</Typography>*/}
                    {/*    <Typography sx={{ fontSize: 28, fontWeight: 700, color: "var(--xp-orange)", lineHeight: 1 }}>{currentLevel}</Typography>*/}
                    {/*</Stack>*/}
                </Stack>

                {/* Progress plate */}
                <Box sx={{ width:"100%", mt: 3, p: 1.25, background: "#fff", border: "1px solid #F7DCD2", borderRadius: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
                        <Stack direction="row" alignItems="baseline" spacing={0.5}>
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "var(--xp-orange)" }}>{xp}</Typography>
                            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>/ {nextThreshold} XP</Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "var(--xp-orange)", lineHeight: 1 }}>LVL {(currentLevel+1)}</Typography>
                    </Stack>
                    <Box sx={{ height: 6, background: "#F7DCD2", borderRadius: 3, overflow: "hidden" }}>
                        <Box sx={{
                            width: `${pct}%`,
                            height: "100%",
                            background: "var(--xp-orange)",
                            borderRadius: 3,
                            transition: "width 400ms ease",
                        }} />
                    </Box>
                </Box>


            </Box>
        </Paper>
    )
}

// PNG shield. Always rendered at full opacity; pure black-and-white when
// the threshold isn't reached yet, full color otherwise.
// Два режима «серости» (обратная совместимость с сезоном 1):
//   - imageGreyUrl задан (сезон 2+) — незаработанный щит показывает СЕРУЮ
//     картинку, CSS-фильтр не применяется;
//   - imageGreyUrl нет (сезон 1) — прежний CSS grayscale поверх imageUrl.
function ShieldImage({ achievement, unlocked }) {
    const hasGreyImage = !!achievement.imageGreyUrl
    const src = hasGreyImage && !unlocked ? achievement.imageGreyUrl : achievement.imageUrl
    return (
        <Box sx={{ textAlign: "center" }}>
            <Box
                component="img"
                src={src}
                alt={achievement.name}
                draggable={false}
                sx={{
                    width: { xs: 70, sm: 90 },
                    height: { xs: 70, sm: 90 },
                    objectFit: "contain",
                    filter: hasGreyImage
                        ? "none"
                        : (unlocked ? "grayscale(0) brightness(120%)" : "grayscale(1)"),
                    transition: "filter 500ms ease",
                }}
            />
            <Typography
                sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: unlocked ? "var(--xp-ink)" : "#8b7d79",
                    mt: 0.5,
                }}
            >
                {shieldCaption(achievement)}
            </Typography>
        </Box>
    )
}

// Числовые щиты («100 XP») подписываются порогом, именные (Expert, Mega Brain,
// XPmaxxer, …) — своим именем.
function shieldCaption(achievement) {
    const name = (achievement.name || "").trim()
    if (!name) return achievement.xpThreshold ?? ""
    return /^\d+\s*XP$/i.test(name) ? (achievement.xpThreshold ?? name) : name
}

// Итог завершённого сезона: «Сезон N — X XP · уровень L» + заработанные щиты
// в уменьшенном виде. Данные — замороженный документ experience того сезона.
function SeasonResultRow({ result }) {
    return (
        <Box sx={{ py: 1.5, "&:not(:last-child)": { borderBottom: "1px solid #F7DCD2" } }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between">
                <Stack direction="row" spacing={1.5} alignItems="baseline">
                    <Typography sx={{ fontWeight: 800, fontSize: 17, color: "var(--xp-orange)" }}>
                        {result.seasonTitle || `Сезон ${result.seasonNumber}`}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                        {result.totalXp} XP
                    </Typography>
                    {result.level > 0 && (
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                            Level {result.level}
                        </Typography>
                    )}
                </Stack>
                {result.achievements?.length > 0 && (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        {result.achievements.map((a, i) => (
                            <Box key={a.name || i} sx={{ textAlign: "center" }} title={a.name}>
                                <Box
                                    component="img"
                                    src={a.imageUrl}
                                    alt={a.name}
                                    draggable={false}
                                    sx={{ width: 44, height: 44, objectFit: "contain" }}
                                />
                            </Box>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Box>
    )
}

export default function ExperiencePage() {
    const { data: snap, isLoading: snapLoading } = useGetExperienceQuery()
    const { data: transactions = [] } = useGetMyExperienceTransactionsQuery()
    const { data: allAchievements = [] } = useGetAllAchievementCategoriesQuery()
    const { data: levels = [] } = useGetExperienceLevelsQuery()
    const { data: xpCategories = [] } = useGetExperienceCategoriesQuery()
    const { data: mascots = [] } = useGetMascotsQuery()
    const { data: activeSeason } = useGetActiveSeasonQuery()
    const { data: seasonResults = [] } = useGetMySeasonResultsQuery()
    const [claim, claimResponse] = useClaimRewardMutation()
    const [celebrationOpen, setCelebrationOpen] = useState(false)
    const [errorOpen, setErrorOpen] = useState(false)

    if (snapLoading || !snap) {
        return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>
    }

    // Shields are total-XP milestone achievements (xpCategoryId == null).
    const shields = [...allAchievements]
        .filter(a => !a.xpCategoryId)
        .sort((a, b) => (a.xpThreshold ?? 0) - (b.xpThreshold ?? 0))

    async function handleClaim() {
        const resp = await claim()
        if (resp.error) {
            setErrorOpen(true)
            return
        }
        setCelebrationOpen(true)
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: "auto", py: 3, px: { xs: 1, sm: 2 } }}>
            {/* Editable header copy (key: experience.header) — managed via Admin → Texts.
                Visuals are decided here — AppCopy returns just the field requested. */}
            <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, mb: 2 }}>
                <Typography component="h3" sx={{ fontSize: { xs: 24, sm: 32 }, fontWeight: 800, color: "#009382", textAlign: "center" }}>
                    <AppCopy appKey="experience.header" field="title" />
                </Typography>
                {/*{activeSeason?.title && (*/}
                {/*    <Box sx={{ textAlign: "center", mt: 1 }}>*/}
                {/*        <Chip*/}
                {/*            label={activeSeason.title}*/}
                {/*            sx={{ fontWeight: 700, color: "#fff", background: "var(--xp-orange)" }}*/}
                {/*        />*/}
                {/*    </Box>*/}
                {/*)}*/}
            </Box>

            {/* Progress bar — single horizontal bar with stripe dividers,
                Nut.svg = current XP, Nut_reward.svg at unclaimed thresholds.
                No labels, no numbers — just the bar and the nuts. */}
            <Box sx={{ pt: 3, pb: 2, px: { xs: 1, sm: 3 } }}>
                <ProgressBar
                    levels={[...levels].sort((a, b) => a.level - b.level)}
                    totalXp={snap.totalXp}
                    lastClaimedLevel={snap.lastClaimedLevel}
                    onClaimClick={handleClaim}
                />
            </Box>

            {/* Shield achievements — six PNGs from public/achievements. */}
            <Box sx={{justifyItems:"center"}}>
                <Box className={"shields-wrapper"} variant="outlined"
                     sx={{ pt:3, pl:5, pr:5, pb:2, mt: 4, borderRadius: 40, maxWidth:"1000px" }}>
                    {shields.length === 0 ? (
                        <Typography color="text.secondary" sx={{ fontSize: 13 }}>No shields configured</Typography>
                    ) : (
                        <Box
                            sx={{
                                display: "grid",
                                // Число колонок следует за размером набора щитов
                                // (6 в сезоне 1, 8 в сезоне 2), а не захардкожено.
                                gridTemplateColumns: {
                                    xs: "repeat(3, 1fr)",
                                    sm: "repeat(4, 1fr)",
                                    md: `repeat(${Math.min(Math.max(shields.length, 1), 8)}, 1fr)`,
                                },
                                gap: { xs: 1, sm: 2, lg: 2 },
                                justifyItems: "center",
                                alignItems: "end",
                            }}
                        >
                            {shields.map(s => (
                                <ShieldImage
                                    key={s.id}
                                    achievement={s}
                                    unlocked={snap.totalXp >= (s.xpThreshold ?? Infinity)}
                                />
                            ))}
                        </Box>
                    )}

                </Box>
            </Box>

            {/* Mascots + linked-category progress (one combined card per mascot).
                Each card shows the bound XP-category's level, current xp, progress
                bar to the next level. No claim button — claims are consolidated on
                the global progress bar above. */}
            {mascots.length > 0 && (
                <Box sx={{ mt: 6 }}>

                    <Grid container spacing={2}>
                        {mascots.map(m => {
                            const cat = m.categoryId
                                ? xpCategories.find(c => c.id === m.categoryId)
                                : null
                            const xp = cat ? ((snap.categoryXp && snap.categoryXp[cat.name]) || 0) : 0
                            return (
                                <Grid item xs={12} sm={12} md={12} lg={6} key={m.id}>
                                    <MascotInfoCard
                                        mascot={m}
                                        category={cat}
                                        xp={xp}
                                        levels={levels}
                                    />
                                </Grid>
                            )
                        })}
                    </Grid>
                </Box>
            )}



            {/* Recent activity */}
            <Paper variant="outlined" sx={{ p: 3, mt: 6, borderRadius: 4 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: "var(--xp-orange)", textTransform: "uppercase", mb: 1 }}>
                    History
                </Typography>
                {transactions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No transactions yet.</Typography>
                ) : (
                    <List dense>
                        {transactions.map(tx => (
                            <ListItem key={tx.id} divider>
                                <ListItemText
                                    primary={
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip size="small" label={txTypeLabel(tx.type)} />
                                            {/* История общая за все сезоны — записи прошлых
                                                сезонов помечаются бейджем. */}
                                            {tx.season && activeSeason && tx.season !== activeSeason.number && (
                                                <Chip size="small" color="default" variant="outlined"
                                                      sx={{ borderStyle: "dashed" }} label={`Season ${tx.season}`} />
                                            )}
                                            {tx.categoryName && <Chip size="small" variant="outlined" label={tx.categoryName} />}
                                            <Typography component="span" sx={{ fontWeight: 700, color: tx.delta > 0 ? "var(--xp-success)" : tx.delta < 0 ? "var(--xp-danger)" : "text.secondary" }}>
                                                {tx.delta > 0 ? `+${tx.delta}` : tx.delta < 0 ? tx.delta : (tx.rewardLevel ? `level ${tx.rewardLevel}` : "—")} {tx.delta !== 0 ? "XP" : ""}
                                            </Typography>
                                        </Stack>
                                    }
                                    secondary={
                                        <>
                                            {tx.comment && <span>{tx.comment} · </span>}
                                            <span>{formatDate(tx.createdAt)}</span>
                                            {tx.authorName && tx.authorName !== "SYSTEM" && <span> · {tx.authorName}</span>}
                                        </>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}

            </Paper>

            <Paper variant="outlined" sx={{ p: 3, mt: 5, borderRadius: 4 }}>
            <Box sx={{color: "text.secondary", mx: "auto", textAlign: "left", fontSize: 14, lineHeight: 1.55, "& p": { my: 0.5 }, "& a": { color: "var(--xp-orange)" } }}>
                <AppCopy appKey="experience.header" field="description" component="div" />
            </Box>
            </Paper>

            {/* Celebration on successful claim */}
            {claimResponse.data && (
                <ClaimCelebration
                    open={celebrationOpen}
                    onClose={() => setCelebrationOpen(false)}
                    claimedLevel={claimResponse.data.claimedLevel}
                    rewardBelcoins={claimResponse.data.rewardBelcoins}
                />
            )}
            {errorOpen && claimResponse.error && (
                <Alert severity="error" onClose={() => setErrorOpen(false)} sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999 }}>
                    {claimResponse.error?.data?.error || "Claim failed"}
                </Alert>
            )}

            {/* Результаты завершённых сезонов — замороженные итоги (XP, уровень,
                щиты) из архивных документов experience. Появляется со 2-го сезона. */}
            {seasonResults.length > 0 && (
                <Paper variant="outlined" sx={{ p: 3, mt: 5, borderRadius: 4 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: "var(--xp-orange)", textTransform: "uppercase", mb: 1 }}>
                        Season results
                    </Typography>
                    {seasonResults.map(r => (
                        <SeasonResultRow key={r.seasonNumber} result={r} />
                    ))}
                </Paper>
            )}
        </Box>
    )
}
